/**
 * SprayJournalSection.tsx v2
 * 
 * Foldbar sprøjtejournal-sektion inde i tildelingslog-kort.
 * Matcher visuelt med "Anvendte mængder" fold-sektionen.
 * 
 * Props:
 *   logId            - ApplicationLog ID
 *   fieldLat/fieldLon - Markens koordinater (til DMI vejr auto-fill)
 *   applicationDate  - Dato for sprøjtning (til DMI observation)
 * 
 * Felter jf. Sprøjtejournalbekendtgørelsen § 2, stk. 2:
 *   Altid: Afgrøde, Sprøjtefører, Sprøjtemidler, Dosering, Vejr (DMI auto)
 *   Betinget: Dysetype+Tryk (afdrift), BBCH (vækststadie), Klokkeslæt (bi-giftig)
 * 
 * SprayJournalProducts tabel er denormaliseret:
 *   ProductName, RegistrationNumber, ActiveSubstance, DosageAmount, DosageUnit,
 *   RequiresDriftReduction, RequiresGrowthStage, IsBeeHarmful, SortOrder
 * 
 * CREATED: 2026-02-13
 * UPDATED: 2026-02-13 v2 - Foldbar sektion + denormaliseret produkt-skema
 */

import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4002';

// ============================================
// TYPER
// ============================================

interface CropType {
  Id: number;
  Name: string;
  Code: string;
  Category: string;
}

interface Product {
  Id: number;
  ProductName: string;
  RegistrationNumber: string;
  ActiveSubstance: string;
  ProductType: string;
  RequiresDriftReduction: boolean;
  DriftReductionPercent: number | null;
  IsBeeHarmful: boolean;
  RequiresGrowthStage: boolean;
  MaxDosage: number | null;
  MaxDosageUnit: string | null;
}

interface JournalProduct {
  ProductRegistryId: number;  // kun til frontend-reference mod ProductRegistry dropdown
  ProductName: string;
  RegistrationNumber: string;
  ActiveSubstance: string | null;
  Dosage: number | null;
  DosageUnit: string;
  RequiresDriftReduction: boolean;
  RequiresGrowthStage: boolean;
  IsBeeHarmful: boolean;
}

interface SprayJournal {
  Id: number;
  ApplicationLogId: number;
  CropType: string;
  OperatorName: string | null;
  StartTime: string | null;
  NozzleType: string | null;
  PressureBar: number | null;
  BBCH: string | null;
  Temperature: number | null;
  WindSpeed: number | null;
  WindDirection: string | null;
  WeatherStationId: string | null;
  WeatherStationName: string | null;
  Status: string | null;
  Products: any[];
}

interface WeatherData {
  temp: number | null;
  windSpeed: number | null;
  windDir: number | null;
  windDirText: string | null;
  station: { id: string; name: string; distanceKm: number };
  observedAt: string | null;
}

interface SprayJournalSectionProps {
  logId: number;
  fieldLat?: number;
  fieldLon?: number;
  applicationDate?: string;
}

// ============================================
// KOMPONENT
// ============================================

const SprayJournalSection: React.FC<SprayJournalSectionProps> = ({
  logId, fieldLat, fieldLon, applicationDate
}) => {
  // Data states
  const [journal, setJournal] = useState<SprayJournal | null>(null);
  const [cropTypes, setCropTypes] = useState<CropType[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  
  // UI states
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  // Form states
  const [cropType, setCropType] = useState('');
  const [operatorName, setOperatorName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<JournalProduct[]>([]);
  const [nozzleType, setNozzleType] = useState('');
  const [pressureBar, setPressureBar] = useState('');
  const [bbch, setBbch] = useState('');
  const [temperature, setTemperature] = useState('');
  const [windSpeed, setWindSpeed] = useState('');
  const [windDirection, setWindDirection] = useState('');
  const [weatherStationId, setWeatherStationId] = useState('');
  const [weatherStationName, setWeatherStationName] = useState('');
  const [weatherObservedAt, setWeatherObservedAt] = useState('');

  // Beregn betingede felter
  const needsDriftReduction = selectedProducts.some(sp => sp.RequiresDriftReduction);
  const needsBBCH = selectedProducts.some(sp => sp.RequiresGrowthStage);
  const hasBeeHarmful = selectedProducts.some(sp => sp.IsBeeHarmful);

  // ============================================
  // API KALD
  // ============================================

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  const loadJournal = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/spray-journal/${logId}`, {
        headers: authHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setJournal(data);
        if (data) {
          setCropType(data.CropType || '');
          setOperatorName(data.OperatorName || '');
          setStartTime(() => {
            if (!data.StartTime) return '';
            const s = String(data.StartTime);
            // Match HH:MM fra enhver format (ISO datetime, time-only, etc)
            const m = s.match(/(\d{1,2}:\d{2})/);
            return m ? m[1].padStart(5, '0') : '';
          });
          setNozzleType(data.NozzleType || '');
          setPressureBar(data.PressureBar != null ? String(data.PressureBar) : '');
          setBbch(data.BBCH || '');
          setTemperature(data.Temperature != null ? String(data.Temperature) : '');
          setWindSpeed(data.WindSpeed != null ? String(data.WindSpeed) : '');
          setWindDirection(data.WindDirection || '');
          setWeatherStationId(data.WeatherStationId || '');
          setWeatherStationName(data.WeatherStationName || '');
          // Map fra DB-kolonnenavne (DosageAmount) til frontend (Dosage)
          setSelectedProducts(data.Products?.map((p: any) => ({
            ProductRegistryId: 0,  // vi har ikke FK i denormaliseret tabel
            ProductName: p.ProductName || '',
            RegistrationNumber: p.RegistrationNumber || '',
            ActiveSubstance: p.ActiveSubstance || null,
            Dosage: p.DosageAmount ?? p.Dosage ?? null,
            DosageUnit: p.DosageUnit || 'L/ha',
            RequiresDriftReduction: !!p.RequiresDriftReduction,
            RequiresGrowthStage: !!p.RequiresGrowthStage,
            IsBeeHarmful: !!p.IsBeeHarmful
          })) || []);
        }
      }
    } catch (err) {
      console.error('Error loading spray journal:', err);
    }
  }, [logId, authHeaders]);

  const loadDropdowns = useCallback(async () => {
    try {
      const [cropsRes, prodsRes] = await Promise.all([
        fetch(`${API_URL}/api/spray-journal/crop-types`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/spray-journal/products/list`, { headers: authHeaders() })
      ]);
      if (cropsRes.ok) setCropTypes(await cropsRes.json());
      if (prodsRes.ok) setProducts(await prodsRes.json());
    } catch (err) {
      console.error('Error loading dropdowns:', err);
    }
  }, [authHeaders]);

  const fetchWeather = useCallback(async () => {
    if (!fieldLat || !fieldLon) return;
    // Sæt tid til middag (12:00 CET) - landmænd sprøjter om dagen
    // ApplicationDate fra DB er typisk "2022-03-08T00:00:00.000Z" (midnat UTC)
    let datetime = applicationDate || new Date().toISOString();
    const dateOnly = datetime.substring(0, 10); // "2022-03-08"
    datetime = dateOnly + 'T11:00:00.000Z';     // 11 UTC = 12 CET middag
    setWeatherLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/weather/observation?lat=${fieldLat}&lon=${fieldLon}&datetime=${datetime}`,
        { headers: authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          const w: WeatherData = data.data;
          if (w.temp != null) setTemperature(String(w.temp));
          if (w.windSpeed != null) setWindSpeed(String(w.windSpeed));
          if (w.windDirText) setWindDirection(w.windDirText);
          if (w.station) {
            setWeatherStationId(w.station.id);
            setWeatherStationName(w.station.name);
          }
          if (w.observedAt) {
            setWeatherObservedAt(w.observedAt);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching weather:', err);
    } finally {
      setWeatherLoading(false);
    }
  }, [fieldLat, fieldLon, applicationDate, authHeaders]);

  const saveJournal = async () => {
    if (!cropType) { setError('Vælg en afgrøde'); return; }
    if (selectedProducts.length === 0) { setError('Tilføj mindst ét sprøjtemiddel'); return; }

    setSaving(true);
    setError(null);
    try {
      const body = {
        CropType: cropType,
        OperatorName: operatorName || null,
        StartTime: startTime || null,
        NozzleType: nozzleType || null,
        PressureBar: pressureBar ? parseFloat(pressureBar) : null,
        BBCH: bbch || null,
        Temperature: temperature ? parseFloat(temperature) : null,
        WindSpeed: windSpeed ? parseFloat(windSpeed) : null,
        WindDirection: windDirection || null,
        WeatherStationId: weatherStationId || null,
        WeatherStationName: weatherStationName || null,
        // Denormaliseret: send produktdata direkte
        Products: selectedProducts.map(sp => ({
          ProductName: sp.ProductName,
          RegistrationNumber: sp.RegistrationNumber,
          ActiveSubstance: sp.ActiveSubstance,
          Dosage: sp.Dosage,
          DosageUnit: sp.DosageUnit || 'L/ha',
          RequiresDriftReduction: sp.RequiresDriftReduction,
          RequiresGrowthStage: sp.RequiresGrowthStage,
          IsBeeHarmful: sp.IsBeeHarmful
        }))
      };

      const res = await fetch(`${API_URL}/api/spray-journal/${logId}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });

      if (res.ok) {
        const saved = await res.json();
        setJournal(saved);
        setIsEditing(false);
      } else {
        const err = await res.json();
        setError(err.error || 'Kunne ikke gemme sprøjtejournal');
      }
    } catch (err) {
      setError('Netværksfejl ved gemning');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // LIFECYCLE
  // ============================================

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadJournal();
      setLoading(false);
    };
    init();
  }, [loadJournal]);

  // Lazy load dropdowns kun ved behov
  useEffect(() => {
    if ((isExpanded && !journal) || isEditing) {
      loadDropdowns();
    }
  }, [isExpanded, isEditing, journal, loadDropdowns]);

  // ============================================
  // PRODUKT HÅNDTERING
  // ============================================

  const addProduct = (productId: number) => {
    if (!productId) return;
    const prod = products.find(p => p.Id === productId);
    if (!prod) return;
    // Tjek duplikat (baseret på RegistrationNumber da vi er denormaliseret)
    if (selectedProducts.some(sp => sp.RegistrationNumber === prod.RegistrationNumber)) return;
    
    setSelectedProducts(prev => [...prev, {
      ProductRegistryId: productId,
      ProductName: prod.ProductName,
      RegistrationNumber: prod.RegistrationNumber,
      ActiveSubstance: prod.ActiveSubstance || null,
      Dosage: null,
      DosageUnit: prod.MaxDosageUnit || 'L/ha',
      RequiresDriftReduction: !!prod.RequiresDriftReduction,
      RequiresGrowthStage: !!prod.RequiresGrowthStage,
      IsBeeHarmful: !!prod.IsBeeHarmful
    }]);
  };

  const removeProduct = (regNumber: string) => {
    setSelectedProducts(prev => prev.filter(sp => sp.RegistrationNumber !== regNumber));
  };

  const updateProductDosage = (regNumber: string, dosage: string) => {
    setSelectedProducts(prev => prev.map(sp =>
      sp.RegistrationNumber === regNumber
        ? { ...sp, Dosage: dosage ? parseFloat(dosage) : null }
        : sp
    ));
  };

  // ============================================
  // CLICK HANDLERS
  // ============================================

  const handleFoldClick = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      if (!journal) setIsEditing(true);
    } else {
      if (isEditing && !journal) {
        setIsEditing(false);
      }
      setIsExpanded(false);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError(null);
    if (journal) loadJournal();
  };

  // ============================================
  // SUMMARY
  // ============================================

  const getSummaryText = (): string => {
    if (loading) return 'Henter...';
    if (!journal) return 'Ikke udfyldt';
    const parts: string[] = [];
    if (journal.CropType) parts.push(journal.CropType);
    if (journal.Products?.length > 0) {
      parts.push(journal.Products.map((p: any) => p.ProductName).join(', '));
    }
    return parts.join(' · ') || 'Kladde';
  };

  // ============================================
  // RENDER: FOLD HEADER
  // ============================================

  const renderFoldHeader = () => (
    <button className="sj-fold-header" onClick={handleFoldClick}>
      <span className="sj-fold-title">Sprøjtejournal</span>
      <div className="sj-fold-right">
        {!isExpanded && (
          <span className="sj-fold-summary">
            {journal ? (
              <>
                <span className={`sj-fold-badge ${
                    journal.Status === 'completed' ? 'sj-badge-done' :
                    journal.Status === 'incomplete' ? 'sj-badge-incomplete' : 'sj-badge-draft'
                  }`}>
                  {journal.Status === 'completed' ? 'Komplet' :
                   journal.Status === 'incomplete' ? 'Mangler data' : 'Kladde'}
                </span>
                {getSummaryText()}
              </>
            ) : (
              <span className="sj-fold-empty">Ikke udfyldt</span>
            )}
          </span>
        )}
        {journal && !isExpanded && (
          <button className="sj-fold-edit" onClick={handleEditClick}>Rediger</button>
        )}
        <span className={`sj-fold-icon ${isExpanded ? 'expanded' : ''}`}>&#9660;</span>
      </div>
    </button>
  );

  // ============================================
  // RENDER: VIEW MODE
  // ============================================

  const renderViewMode = () => (
    <div className="sj-content">
      <div className="sj-view-fields">
        <div className="sj-row">
          <span className="sj-lbl">Afgrøde</span>
          <span className="sj-val">{journal!.CropType}</span>
        </div>
        {journal!.OperatorName && (
          <div className="sj-row">
            <span className="sj-lbl">Sprøjtefører</span>
            <span className="sj-val">{journal!.OperatorName}</span>
          </div>
        )}
        {journal!.Products && journal!.Products.length > 0 && journal!.Products.map((p: any, i: number) => (
          <div key={i} className="sj-row">
            <span className="sj-lbl">{i === 0 ? 'Sprøjtemidler' : ''}</span>
            <span className="sj-val">{p.ProductName} {(p.DosageAmount ?? p.Dosage) != null ? `${p.DosageAmount ?? p.Dosage} ${p.DosageUnit || 'L/ha'}` : ''}</span>
          </div>
        ))}
        {(journal!.Temperature != null || journal!.WindSpeed != null) && (
          <div className="sj-row">
            <span className="sj-lbl">Vejr</span>
            <span className="sj-val">
              {journal!.Temperature != null ? `${journal!.Temperature}°C` : ''}
              {journal!.WindSpeed != null ? ` · ${journal!.WindSpeed} m/s ${journal!.WindDirection || ''}` : ''}
            </span>
          </div>
        )}
        {journal!.NozzleType && (
          <div className="sj-row">
            <span className="sj-lbl">Dysetype</span>
            <span className="sj-val">{journal!.NozzleType}{journal!.PressureBar ? ` · ${journal!.PressureBar} bar` : ''}</span>
          </div>
        )}
        {journal!.BBCH && (
          <div className="sj-row">
            <span className="sj-lbl">BBCH</span>
            <span className="sj-val">{journal!.BBCH}</span>
          </div>
        )}
        {journal!.StartTime && (
          <div className="sj-row">
            <span className="sj-lbl">Klokkeslæt</span>
            <span className="sj-val">{(() => {
                    const s = String(journal!.StartTime || '');
                    const m = s.match(/(\d{1,2}:\d{2})/);
                    return m ? m[1].padStart(5, '0') : '';
                  })()}</span>
          </div>
        )}
      </div>
      <button className="sj-edit-inline" onClick={() => setIsEditing(true)}>Rediger</button>
    </div>
  );

  // ============================================
  // RENDER: EDIT MODE
  // ============================================

  const renderEditMode = () => (
    <div className="sj-content sj-content-edit">
      {error && <div className="sj-error">{error}</div>}

      <div className="sj-fg">
        <label className="sj-fl">Afgrøde *</label>
        <select className="sj-sel" value={cropType} onChange={e => setCropType(e.target.value)}>
          <option value="">Vælg afgrøde...</option>
          {cropTypes.map(ct => (
            <option key={ct.Id} value={ct.Name}>{ct.Name} ({ct.Code})</option>
          ))}
        </select>
      </div>

      <div className="sj-fg">
        <label className="sj-fl">Sprøjtefører *</label>
        <input className="sj-inp" type="text" value={operatorName}
          onChange={e => setOperatorName(e.target.value)} placeholder="Navn" />
      </div>

      <div className="sj-fg">
        <label className="sj-fl">Sprøjtemiddel *</label>
        <select className="sj-sel" value=""
          onChange={e => { if (e.target.value) addProduct(parseInt(e.target.value)); }}>
          <option value="">Tilføj sprøjtemiddel...</option>
          {products.map(p => (
            <option key={p.Id} value={p.Id}
              disabled={selectedProducts.some(sp => sp.RegistrationNumber === p.RegistrationNumber)}>
              {p.ProductName} ({p.RegistrationNumber})
            </option>
          ))}
        </select>

        {selectedProducts.map(sp => (
          <div key={sp.RegistrationNumber} className="sj-prod-entry">
            <div className="sj-prod-top">
              <span className="sj-prod-name">{sp.ProductName}</span>
              {sp.RequiresDriftReduction && <span className="sj-tag sj-tag-drift">Afdrift</span>}
              {sp.IsBeeHarmful && <span className="sj-tag sj-tag-bee">Bi-giftig</span>}
              <button className="sj-rm" onClick={() => removeProduct(sp.RegistrationNumber)}>&#215;</button>
            </div>
            <div className="sj-dose-row">
              <input className="sj-inp sj-inp-sm" type="number" step="0.01"
                value={sp.Dosage ?? ''}
                onChange={e => updateProductDosage(sp.RegistrationNumber, e.target.value)}
                placeholder="Dosering" />
              <span className="sj-dose-unit">{sp.DosageUnit || 'L/ha'}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="sj-fg">
        <div className="sj-weather-hdr">
          <label className="sj-fl">Vejrforhold</label>
          <button className="sj-weather-btn" onClick={fetchWeather}
            disabled={weatherLoading || !fieldLat || !fieldLon}>
            {weatherLoading ? 'Henter...' : 'Hent fra DMI'}
          </button>
        </div>
        <div className="sj-weather-grid">
          <div className="sj-wf">
            <label>Temp °C</label>
            <input className="sj-inp sj-inp-sm" type="number" step="0.1"
              value={temperature} onChange={e => setTemperature(e.target.value)} />
          </div>
          <div className="sj-wf">
            <label>Vind m/s</label>
            <input className="sj-inp sj-inp-sm" type="number" step="0.1"
              value={windSpeed} onChange={e => setWindSpeed(e.target.value)} />
          </div>
          <div className="sj-wf">
            <label>Retning</label>
            <input className="sj-inp sj-inp-sm" type="text"
              value={windDirection} onChange={e => setWindDirection(e.target.value)}
              placeholder="N, NØ..." />
          </div>
        </div>
        {weatherStationName && (
          <div className="sj-weather-src">DMI: {weatherStationName}{weatherObservedAt ? ` (${new Date(weatherObservedAt).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })})` : ''}</div>
        )}
      </div>

      {needsDriftReduction && (
        <div className="sj-fg sj-cond">
          <label className="sj-fl">Dysetype og tryk <span className="sj-cond-tag">Afdriftsreduktion</span></label>
          <div className="sj-two">
            <input className="sj-inp" type="text" value={nozzleType}
              onChange={e => setNozzleType(e.target.value)} placeholder="Fx ID 120-03" />
            <div className="sj-inp-unit">
              <input className="sj-inp sj-inp-sm" type="number" step="0.1"
                value={pressureBar} onChange={e => setPressureBar(e.target.value)} placeholder="Tryk" />
              <span>bar</span>
            </div>
          </div>
        </div>
      )}

      {needsBBCH && (
        <div className="sj-fg sj-cond">
          <label className="sj-fl">BBCH vækststadie <span className="sj-cond-tag">Påkrævet</span></label>
          <input className="sj-inp" type="text" value={bbch}
            onChange={e => setBbch(e.target.value)} placeholder="Fx 30-32" />
        </div>
      )}

      {hasBeeHarmful && (
        <div className="sj-fg sj-cond">
          <label className="sj-fl">Klokkeslæt <span className="sj-cond-tag">Bi-giftig</span></label>
          <input className="sj-inp sj-inp-sm" type="time" value={startTime}
            onChange={e => setStartTime(e.target.value)} />
        </div>
      )}

      <div className="sj-btns">
        <button className="sj-save" onClick={saveJournal} disabled={saving}>
          {saving ? 'Gemmer...' : 'Gem sprøjtejournal'}
        </button>
        <button className="sj-cancel" onClick={handleCancelEdit}>Annuller</button>
      </div>
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div className="sj-wrapper">
      {renderFoldHeader()}
      {isExpanded && (
        isEditing ? renderEditMode() : (journal ? renderViewMode() : null)
      )}
      <style>{sjStyles}</style>
    </div>
  );
};

// ============================================
// STYLES
// ============================================

const sjStyles = `
  .sj-wrapper { margin-top: 8px; }
  .sj-fold-header {
    display: flex; justify-content: space-between; align-items: center;
    width: 100%; padding: 10px 12px; background: white;
    border: 1px solid #16a34a; border-radius: 6px;
    cursor: pointer; transition: background 0.15s; font-family: inherit;
    overflow: hidden; box-sizing: border-box;
  }
  .sj-fold-header:hover { background: #f0fdf4; }
  .sj-fold-title { font-size: 13px; font-weight: 500; color: #374151; }
  .sj-fold-right { display: flex; align-items: center; gap: 6px; overflow: hidden; flex-shrink: 1; min-width: 0; }
  .sj-fold-summary { font-size: 11px; color: #6b7280; display: flex; align-items: center; gap: 4px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; min-width: 0; flex-shrink: 1; }
  .sj-fold-empty { color: #9ca3af; font-style: italic; }
  .sj-fold-badge { font-size: 10px; padding: 1px 6px; border-radius: 3px; font-weight: 500; }
  .sj-badge-draft { background: #f3f4f6; color: #6b7280; }
  .sj-badge-incomplete { background: #fef3c7; color: #92400e; }
  .sj-badge-done { background: #dcfce7; color: #16a34a; }
  .sj-fold-edit {
    font-size: 11px; padding: 2px 8px; background: white;
    border: 1px solid #d1d5db; border-radius: 4px; color: #16a34a; cursor: pointer;
  }
  .sj-fold-edit:hover { background: #f0fdf4; border-color: #16a34a; }
  .sj-fold-icon { font-size: 10px; color: #9ca3af; transition: transform 0.2s; }
  .sj-fold-icon.expanded { transform: rotate(180deg); }

  .sj-content { padding: 12px; background: white; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 6px 6px; }
  .sj-content-edit { display: flex; flex-direction: column; gap: 12px; border-color: #16a34a; }

  .sj-view-fields { display: flex; flex-direction: column; gap: 6px; }
  .sj-row { display: flex; justify-content: space-between; align-items: flex-start; font-size: 12px; }
  .sj-row-col { flex-direction: column; gap: 2px; }
  .sj-lbl { color: #6b7280; min-width: 90px; flex-shrink: 0; }
  .sj-val { color: #111827; text-align: right; flex: 1; }
  .sj-prod-line { display: flex; justify-content: space-between; font-size: 12px; padding: 2px 0 2px 90px; color: #374151; }
  .sj-prod-dose { color: #6b7280; font-weight: 500; }
  .sj-edit-inline {
    margin-top: 8px; padding: 6px 12px; background: white;
    border: 1px solid #d1d5db; border-radius: 5px; font-size: 12px;
    color: #16a34a; cursor: pointer; align-self: flex-start;
  }
  .sj-edit-inline:hover { background: #f0fdf4; border-color: #16a34a; }

  .sj-error { padding: 6px 10px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 5px; color: #dc2626; font-size: 12px; }
  .sj-fg { display: flex; flex-direction: column; gap: 4px; }
  .sj-fl { font-size: 11px; font-weight: 600; color: #374151; }
  .sj-sel, .sj-inp {
    padding: 7px 9px; border: 1px solid #d1d5db; border-radius: 5px;
    font-size: 12px; color: #111827; background: white;
    width: 100%; box-sizing: border-box; font-family: inherit;
  }
  .sj-sel:focus, .sj-inp:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 2px rgba(22,163,74,0.12); }
  .sj-inp-sm { width: 90px; }

  .sj-prod-entry { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 5px; padding: 6px 8px; margin-top: 4px; }
  .sj-prod-top { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
  .sj-prod-name { font-size: 12px; font-weight: 500; flex: 1; color: #374151; }
  .sj-tag { font-size: 9px; padding: 1px 5px; border-radius: 3px; font-weight: 500; }
  .sj-tag-drift { background: #dbeafe; color: #1e40af; }
  .sj-tag-bee { background: #fef3c7; color: #92400e; }
  .sj-rm { background: none; border: none; color: #dc2626; font-size: 16px; cursor: pointer; padding: 0 3px; line-height: 1; }
  .sj-dose-row { display: flex; align-items: center; gap: 5px; margin-top: 4px; }
  .sj-dose-unit { font-size: 11px; color: #6b7280; }

  .sj-weather-hdr { display: flex; justify-content: space-between; align-items: center; }
  .sj-weather-btn {
    font-size: 10px; padding: 2px 7px; background: #dbeafe;
    border: 1px solid #93c5fd; border-radius: 3px; color: #1d4ed8; cursor: pointer;
  }
  .sj-weather-btn:hover { background: #bfdbfe; }
  .sj-weather-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .sj-weather-grid { display: flex; gap: 6px; }
  .sj-wf { flex: 1; display: flex; flex-direction: column; gap: 2px; }
  .sj-wf label { font-size: 10px; color: #6b7280; }
  .sj-wf .sj-inp-sm { width: 100%; }
  .sj-weather-src { font-size: 10px; color: #9ca3af; margin-top: 2px; }

  .sj-cond { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 5px; padding: 8px; }
  .sj-cond-tag { font-size: 9px; padding: 1px 5px; background: #e0f2fe; color: #0369a1; border-radius: 3px; font-weight: 400; margin-left: 4px; }
  .sj-two { display: flex; gap: 6px; }
  .sj-two .sj-inp { flex: 1; }
  .sj-inp-unit { display: flex; align-items: center; gap: 3px; }
  .sj-inp-unit span { font-size: 11px; color: #6b7280; }

  .sj-btns { display: flex; gap: 6px; margin-top: 2px; }
  .sj-save {
    flex: 1; padding: 8px; background: #16a34a; color: white;
    border: none; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer;
  }
  .sj-save:hover { background: #15803d; }
  .sj-save:disabled { opacity: 0.5; cursor: not-allowed; }
  .sj-cancel {
    padding: 8px 14px; background: white; border: 1px solid #d1d5db;
    border-radius: 6px; font-size: 13px; color: #6b7280; cursor: pointer;
  }
  .sj-cancel:hover { background: #f9fafb; }
`;

export default SprayJournalSection;
